import { vec2 } from '@d2/core';
import {
  type GroundItemState,
  type LevelState,
  type MonsterState,
  type PlayerState,
  type WorldState,
  bodyActionOf,
  championTierIdOf,
  dungeonParams,
  eliteAffixIdsOf,
  groundCell,
  groundKindOf,
  itemRarityIdOf,
  levelStairs,
  lifeFraction,
  monsterArchetypeOf,
  type RunTollPayload,
} from '@d2/schema';

import { type LevelDescriptor } from './cave';
import { type CharacterVitals } from './character-feed';
import { type RunToll } from './run-end';
import {
  type GroundItemSnapshot,
  type MonsterSnapshot,
  type PlayerSnapshot,
  type WorldSnapshot,
} from './session';

/**
 * The wire, read once, at the edge.
 *
 * `@d2/schema` owns what crosses the socket and `./session` owns what the
 * client does about it, and neither should know the other's shape. This module
 * is the seam: decorated `@colyseus/schema` classes in, plain readonly values
 * out, and nothing else at all — no clock, no buffers, no rules.
 *
 * It is worth its own file for two reasons. Everything downstream stays
 * testable without `@colyseus/schema`, which is why `interpolation.ts` and
 * `prediction.ts` declared their own input shapes in the first place
 * (`TODO.md` #185). And the three places the wire is *interpreted* rather than
 * copied are all here, together, where they can be seen at once:
 *
 *   - **`walking` decides whether a destination exists at all.** The wire
 *     leaves `destinationX/Y` stale after a walk ends on purpose — zeroing them
 *     would cost two field changes to overwrite a value the flag already says
 *     to ignore (`state.ts`, `clearDestination`) — so reading them without the
 *     flag would point a client at the cell of a walk that finished.
 *   - **`dungeonParams` maps the level's four numbers back onto core's own
 *     options object**, which is `@d2/schema`'s function rather than a mapping
 *     written here, so the day core grows an option this stops compiling
 *     instead of silently defaulting.
 *   - **A `MapSchema` becomes a list in one place.** The session rebuilds its
 *     entity map from whatever the snapshot contains, so who is on the floor is
 *     answered by this iteration and nothing else.
 *
 * **Messages are read here as well as state, and `readRunToll` is the first of
 * them.** What crosses this seam is not always a decorated schema class: a
 * `SERVER_MESSAGE` arrives as whatever msgpack decoded, addressed to one
 * connection rather than replicated to the room. It is read in this file anyway,
 * and for the reason everything else is — `room-link.ts` is the socket, it is
 * excluded from this package's coverage because it holds no state and makes no
 * decision, and a reader written *there* would be the one piece of wire-reading
 * no unit test could reach. So the adapter subscribes and this module converts,
 * exactly as it does for a patch.
 *
 * The one difference worth stating, because the next message will inherit it:
 * a schema field was decoded into a declared type and a message payload was
 * not, so `RunTollPayload` is a promise the sender made rather than a shape the
 * decoder enforced. Nothing is validated here on the strength of that — this is
 * the server reporting what it has already done, `pong` and `progression` are
 * not parsed either, and a parser with no case to refuse is a parser that goes
 * stale. Where the promise is actually stopped from reaching a player is the
 * *sentence* (`./run-end`'s formatter refuses to print a figure that is not a
 * number), which is a decision about words and belongs where the words are.
 *
 * Monsters add a fourth, and it is the same kind of thing: **a code becomes a
 * name here and nowhere downstream.** `monsterArchetypeOf`, `championTierIdOf`,
 * `eliteAffixIdsOf` and `lifeFraction` are `@d2/schema`'s own readers — the
 * client's half of a reference whose *table* stays in `@d2/core` — so the
 * renderer is handed archetypes, tier ids, affix ids and a fraction, and never a
 * number it would have to know a layout to interpret. Nothing here looks any of
 * those ids up: what a tier multiplies and what an affix does are the server's
 * to apply and core's to define, and a client that decided either would be the
 * duplicated rule this architecture exists to prevent.
 */

/**
 * The floor a room is on, off the wire.
 *
 * A fifth thing is interpreted rather than copied here, and it is the same kind
 * of thing `dungeonParams` is: **two `int16` fields become the one `Vec2` core
 * names a cell with**, through `@d2/schema`'s own `levelStairs` rather than a
 * pair read by hand — so nothing downstream has to remember which of them is
 * which, and the comparison this client owes the descriptor (`./cave`'s
 * `regrowFloor`) is written against the shape `placeStairs` hands back.
 *
 * It is the only field on this descriptor that is not an *input* to the
 * generator. Everything else is re-run; this is an answer, published because
 * the client cannot reproduce it — the exit is a function of the body it is
 * solved for and no radius crosses this wire (`@d2/schema`'s
 * `LevelState.stairs`, `TODO.md` #479).
 */
export const readLevel = (level: LevelState): LevelDescriptor => ({
  seed: level.seed,
  depth: level.depth,
  params: dungeonParams(level),
  gridHash: level.gridHash,
  stairs: levelStairs(level),
});

/**
 * One replicated player, off the wire.
 *
 * The three-fields-into-one fold `prediction.ts` asks its caller for:
 * `walking ? vec2(destinationX, destinationY) : null`.
 *
 * `action` is the fifth code that becomes a name here and nowhere downstream,
 * and `bodyActionOf` is `@d2/schema`'s own reader — so the renderer is handed
 * *"striking"* rather than a number it would have to know a list to interpret.
 * It throws on a code no release of this client has ever held, and that is left
 * to propagate for the reason `monsterArchetypeOf`'s throw is: the fallback
 * anybody would reach for is `idle`, which is *"draw the swinging monster
 * standing still"* — the exact bug the field exists to fix.
 */
export const readPlayer = (player: PlayerState): PlayerSnapshot => ({
  id: player.id,
  position: vec2(player.x, player.y),
  destination: player.walking ? vec2(player.destinationX, player.destinationY) : null,
  lastSequence: player.lastSequence,
  action: bodyActionOf(player.action),
});

/**
 * What one seat's **character** is doing, off the wire.
 *
 * A second reader of `PlayerState` beside `readPlayer`, and the split is the one
 * `session.ts` and `character-feed.ts` already draw between them: `readPlayer`
 * answers where a *body* is, which is interpolated, predicted and reconciled;
 * this answers how a *character* is doing, which is none of those things and is
 * simply the newest thing the server said. Folding six more fields into
 * `PlayerSnapshot` would put a life total on the prediction's timeline and make
 * every position fixture in the suite carry an orb.
 *
 * Nothing is interpreted here — unlike `walking`, which decides whether a
 * destination exists at all, every one of these six is a number the server wrote
 * and the HUD draws. What a level *costs* and how much of it is spent inside the
 * current one are core's `experienceForLevel` and `experienceToNextLevel`, run on
 * the client exactly as `findPath` is (`@d2/schema`'s `ProgressionPayload`).
 */
export const readVitals = (player: PlayerState): CharacterVitals => ({
  life: player.life,
  maximumLife: player.maxLife,
  mana: player.mana,
  maximumMana: player.maxMana,
  level: player.level,
  experience: player.experience,
});

/**
 * One replicated monster, off the wire.
 *
 * The id is the caller's because `MonsterState` does not carry one: the map key
 * *is* the id, which is 258 bytes of every join saved at sixty bodies
 * (`packages/schema/src/monsters.ts`) and the reason this signature differs from
 * `readPlayer`'s.
 *
 * `monsterArchetypeOf` throws on a body the server never described, and that is
 * left to propagate. A monster with no archetype is a room that failed to
 * describe what it spawned; drawing it as a melee one would hide that forever,
 * and this is the edge where the wire is supposed to be checked.
 */
export const readMonster = (id: string, monster: MonsterState): MonsterSnapshot => ({
  id,
  position: vec2(monster.x, monster.y),
  walking: monster.walking,
  archetype: monsterArchetypeOf(monster.archetype),
  life: lifeFraction(monster),
  champion: championTierIdOf(monster.champion),
  affixes: eliteAffixIdsOf(monster.affixes),
  leader: monster.leader,
  // Read beside `walking` rather than instead of it: the flag is the mechanical
  // fact the interpolator depends on and this is the *visual* reading of the
  // same truth (`packages/schema/src/actions.ts`). A body walking towards
  // something it is mid-swing at is both, and the field says the more specific.
  action: bodyActionOf(monster.action),
});

/**
 * One thing lying on the floor, off the wire.
 *
 * The id is the caller's for the same reason `readMonster`'s is: the map key
 * *is* the id, so an entry carries none — and that id is exactly what a
 * `pickUp` names, which is what makes "a cell with three drops on it" a thing a
 * player can point at (`@d2/schema`'s `PickUpPayload`).
 *
 * Two codes become names here and nowhere downstream. `groundKindOf` throws on
 * an entry no server described, and that is left to propagate — the same edge
 * `readMonster` refuses at, for the same reason: an undescribed drop rendered as
 * gear would hide a broken room forever. `itemRarityIdOf` answers `null` for the
 * three kinds that have no tier, which the renderer needs in order to draw a
 * potion as a potion rather than as the first tier.
 */
export const readGroundItem = (id: string, item: GroundItemState): GroundItemSnapshot => ({
  id,
  cell: groundCell(item),
  kind: groundKindOf(item.kind),
  rarity: itemRarityIdOf(item.rarity),
  baseId: item.baseId,
  gold: item.gold,
});

/**
 * What one death cost the seat that paid it, off the message that says so.
 *
 * The first thing read here that is not state, and the shape the next addressed
 * message copies: the payload goes in, the plain value `./run-end` declares
 * comes out, and the two are held against each other by the annotation on this
 * signature rather than by a comment. `run-end.ts` owns `RunToll` because it
 * owns what a run ending *means* — the code, the words, and now the bill — and
 * that keeps this module free of anything downstream of the wire, exactly as
 * `readVitals` keeps `character-feed.ts` free of `@d2/schema`.
 *
 * Nothing is interpreted, and unlike `readVitals` that is not for want of
 * looking: neither number means anything this client could re-derive. `gold` is
 * a proportion of a purse as it stood at a moment nobody here observed, and
 * `items` is a count whose contents were never sent. Both are simply the newest
 * — and only — thing the server said about a bill it has already charged.
 */
export const readRunToll = (toll: RunTollPayload): RunToll => ({ gold: toll.gold, items: toll.items });

/** One tick of the room, off the wire. */
export const readWorld = (state: WorldState): WorldSnapshot => {
  const players: PlayerSnapshot[] = [];
  const monsters: MonsterSnapshot[] = [];
  const ground: GroundItemSnapshot[] = [];

  state.players.forEach((player: PlayerState) => {
    players.push(readPlayer(player));
  });

  state.monsters.forEach((monster: MonsterState, id: string) => {
    monsters.push(readMonster(id, monster));
  });

  state.ground.forEach((item: GroundItemState, id: string) => {
    ground.push(readGroundItem(id, item));
  });

  return { tick: state.tick, players, monsters, ground };
};
